import React, { Component } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import '../static/css/SignUpInfo.css';
import '../static/css/CreateEventOpportunity.css';
import logo from '../static/images/images/a-logo-transperent.png';
import Autocomplete from 'react-google-autocomplete';
import { readLocal } from '../safeStorage';

// Post-signup +20 Power Level event-creation bonus. Reachable only with a prelaunch_token
// (set by the paid /signup-cs and community signup flows) — drives early event creation.
class CreateEventOpportunity extends Component {
    constructor() {
        super();
        this.state = {
            title: '',
            description: '',
            event_date: '',
            event_time: '',
            end_time: '',
            location: '',
            location_address: '',
            location_city: '',
            location_state: '',
            location_zip: '',
            location_country: '',
            location_lat: null,
            location_lng: null,
            vibes: [],
            max_allowed: 'No limit',
            age_ranges: ['Any age'],
            whos_welcome: ['Anyone'],
            account_types: 'Anyone',
            isSubmitting: false,
        };
        this.onSubmit = this.onSubmit.bind(this);
    }

    toggleSelection(field, value, max, defaultValue) {
        const current = this.state[field];

        // If tapping the default option, reset to just the default
        if (defaultValue && value === defaultValue) {
            this.setState({ [field]: [defaultValue] });
            return;
        }

        // If tapping a non-default option, remove the default if it's selected
        let updated = current.filter(v => v !== defaultValue);

        if (updated.includes(value)) {
            updated = updated.filter(v => v !== value);
            // If nothing left, go back to default
            if (updated.length === 0 && defaultValue) {
                updated = [defaultValue];
            }
        } else {
            if (updated.length < max) {
                updated = [...updated, value];
            }
        }

        this.setState({ [field]: updated });
    }

    async onSubmit(event) {
        event.preventDefault();

        const token = localStorage.getItem('token');
        if (!token) {
            alert("Please sign in to create an event.");
            window.location = "/signin";
            return;
        }

        // Validate required fields
        if (!this.state.title.trim()) { alert("Please enter an event title."); return; }
        if (!this.state.description.trim()) { alert("Please add event details."); return; }
        if (!this.state.event_date) { alert("Please select an event date."); return; }
        if (!this.state.event_time) { alert("Please select an event time."); return; }
        if (!this.state.location) { alert("Please select an event location."); return; }

        // Format time to HH:MM:SS
        const formatTime = (t) => t ? (t.length === 5 ? t + ':00' : t) : null;

        const eventData = {
            event_title: this.state.title,
            event_description: this.state.description,
            event_date: this.state.event_date,
            event_time: formatTime(this.state.event_time),
            event_end_time: formatTime(this.state.end_time) || null,
            latitude: this.state.location_lat,
            longitude: this.state.location_lng,
            zip_code: this.state.location_zip || '',
            city: this.state.location_city,
            state: this.state.location_state,
            country_code: this.state.location_country,
            full_address: this.state.location,
            who_can_join: this.state.account_types,
            max_allowed: this.state.max_allowed === 'No limit' ? 0 : parseInt(this.state.max_allowed),
            vibe_tags: this.state.vibes,
            age_ranges: this.state.age_ranges,
            whos_welcome: this.state.whos_welcome,
        };

        this.setState({ isSubmitting: true });

        try {
            await axios.post(process.env.REACT_APP_SERVER_API + '/events/pre-create-event', eventData, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            localStorage.removeItem('prelaunch_token');
            localStorage.removeItem('signupFormData');
            localStorage.setItem('event_bonus_claimed', '1');
            alert("Event created successfully! You earned +20 Power Level!");
            window.location = "/home";
        } catch (err) {
            const data = err.response && err.response.data ? err.response.data : {};
            alert(data.error || data.message || "Something went wrong creating your event. Please try again.");
            this.setState({ isSubmitting: false });
        }
    }

    render() {
        const vibeOptions = [
            { emoji: '🍜', label: '🍜 Food' },
            { emoji: '🍸', label: '🍸 Drinks' },
            { emoji: '☕', label: '☕ Coffee' },
            { emoji: '🧋', label: '🧋 Boba' },
            { emoji: '🏯', label: '🏯 Convention' },
            { emoji: '📺', label: '📺 Watch Party' },
            { emoji: '🎮', label: '🎮 Gaming' },
            { emoji: '🎤', label: '🎤 Karaoke' },
            { emoji: '🚶', label: '🚶 Outing' },
            { emoji: '🌃', label: '🌃 Night Hang' },
            { emoji: '🎲', label: '🎲 Game Night' },
            { emoji: '🌹', label: '🌹 Date' },
            { emoji: '💕', label: '💕 Double Date' },
            { emoji: '🤝', label: '🤝 Casual Hangout' },
        ];

        const ageOptions = ['Any age', '18-21', '21-25', '25-30', '30-35', '35-40', '40-50', '50+'];

        const welcomeOptions = [
            'Anyone', 'Male only', 'Female only', 'Nonbinary',
            'Any gender welcome', '18+ only', '21+ only',
            'Couples welcome', 'New members welcome', 'Cosplay welcome', 'Family friendly'
        ];

        const accountTypeOptions = ['Anyone', 'Verified', 'Premium', 'Verified + Premium'];

        const accountTypeDescriptions = {
            'Anyone': 'Open to everyone. Best for building community and discovery.',
            'Verified': 'Only users with a verified badge can join this event. Great for safety-focused meetups.',
            'Premium': 'Only premium members can join. Useful for smaller, committed groups.',
            'Verified + Premium': 'Verified AND premium members can join. Combines trust + commitment.',
        };

        const maxOptions = ['No limit', ...Array.from({ length: 25 }, (_, i) => String(i + 1))];

        // Earliest selectable event date = today (local). Keeps dates out of the past without a hardcoded gate.
        const _now = new Date();
        const minEventDate = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}-${String(_now.getDate()).padStart(2, '0')}`;

        // One-time bonus: once they've created their event (claimed the +20), don't let them back onto
        // this page — send them home. Gating on "claimed" (not "visited") keeps refresh-during-fill safe.
        if (readLocal('event_bonus_claimed')) {
            return <Navigate to="/home" replace />;
        }

        if (!readLocal('token')) {
            return (
                <div className="signup-info-container" style={{ fontSize: "10px" }}>
                    <div>
                        <div className="animates-logo-container"><Link to={'/'}><img className="animates-logo hover" src={logo} alt="" /></Link></div>
                        <div className="signup-info-box" style={{ textAlign: "center", marginTop: "50px" }}>
                            <h3 className="signup-info-title" style={{ color: "#85C7DD" }}><u>Oops!</u></h3>
                            <p style={{ fontSize: "13px", marginTop: "20px", color: "white" }}>Please sign in to create an event and earn your Power Level boost!</p>
                            <Link to="/signin" className="no-dec"><button className="signup-confirmation-continue" style={{ marginTop: "20px", backgroundColor: "black" }}>Sign In</button></Link>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="signup-info-container" style={{ fontSize: "10px" }}>
                <div>
                    <div className="animates-logo-container"><Link to={'/coming-soon'}><img className="animates-logo hover" src={logo} alt="" /></Link></div>
                    <div className="signup-info-box create-event-form">
                        <h3 className="signup-info-title" style={{ marginBottom: "20px", color: "#ED9594" }}><u>Create Event</u></h3>
                        <p style={{ textAlign: "center", fontSize: "13px", marginBottom: "30px" }}>This will give you <span role="img" aria-label="">⚡️</span> <strong>+20 Power Level</strong> gain! Create a meetup, date, event — anything goes. A boba meetup, anime watch party, game night, date night, or even a Discord hangout. If you can think it, you can create it!</p>

                        <form onSubmit={this.onSubmit}>

                            {/* Title */}
                            <input
                                className="form-input"
                                type="text"
                                placeholder="Title"
                                maxLength={50}
                                value={this.state.title}
                                onChange={(e) => this.setState({ title: e.target.value })}
                            />
                            <p style={{ textAlign: "right", width: "85%", margin: "-10px auto 0 auto", color: "#999", fontSize: "10px" }}>{50 - this.state.title.length} characters left</p>

                            {/* Description */}
                            <p style={{ textAlign: "left", width: "85%", margin: "15px auto -10px auto", fontSize: "12px" }}>Add as much as details as you can about this event:</p>
                            <textarea
                                className="form-input"
                                placeholder="Type here..."
                                maxLength={1000}
                                value={this.state.description}
                                onChange={(e) => this.setState({ description: e.target.value })}
                                style={{ height: "120px", resize: "vertical", textAlign: "left", paddingTop: "10px", paddingLeft: "10px" }}
                            />
                            <p style={{ textAlign: "right", width: "85%", margin: "-10px auto 0 auto", color: "#999", fontSize: "10px" }}>{1000 - this.state.description.length} characters left</p>

                            {/* Event Date */}
                            <p style={{ textAlign: "left", width: "85%", margin: "15px auto 0px auto", fontSize: "12px" }}>Event Date:</p>
                            <p style={{ textAlign: "left", width: "85%", margin: "0 auto -10px auto", color: "#999", fontSize: "9px" }}>Pick any date from today onward.</p>
                            <input
                                className="form-input"
                                type="date"
                                min={minEventDate}
                                value={this.state.event_date}
                                onChange={(e) => this.setState({ event_date: e.target.value })}
                            />

                            {/* Event Time */}
                            <p style={{ textAlign: "left", width: "85%", margin: "15px auto -10px auto", fontSize: "12px" }}>Event Time:</p>
                            <input
                                className="form-input"
                                type="time"
                                value={this.state.event_time}
                                onChange={(e) => this.setState({ event_time: e.target.value })}
                            />

                            {/* End Time (Optional) */}
                            <p style={{ textAlign: "left", width: "85%", margin: "15px auto -10px auto", fontSize: "12px" }}>End Time (Optional):</p>
                            <input
                                className="form-input"
                                type="time"
                                placeholder="Optional"
                                value={this.state.end_time}
                                onChange={(e) => this.setState({ end_time: e.target.value })}
                            />

                            {/* Event Location */}
                            <p style={{ textAlign: "left", width: "85%", margin: "15px auto -10px auto", fontSize: "12px" }}>Event Location:</p>
                            <Autocomplete
                                className="form-input"
                                apiKey="AIzaSyA5B2aRYvF2sgrVzORmMzvaBvbIqTq7--o"
                                onPlaceSelected={(place) => {
                                    const components = place.address_components || [];
                                    const get = (type) => {
                                        const c = components.find(c => c.types.includes(type));
                                        return c ? c.long_name : '';
                                    };
                                    const getShort = (type) => {
                                        const c = components.find(c => c.types.includes(type));
                                        return c ? c.short_name : '';
                                    };
                                    this.setState({
                                        location: place.formatted_address || '',
                                        location_address: get('street_number') + ' ' + get('route'),
                                        location_city: get('locality') || get('sublocality_level_1'),
                                        location_state: getShort('administrative_area_level_1'),
                                        location_zip: get('postal_code'),
                                        location_country: getShort('country'),
                                        location_lat: place.geometry?.location?.lat(),
                                        location_lng: place.geometry?.location?.lng(),
                                    });
                                }}
                                options={{
                                    types: ['establishment', 'geocode'],
                                }}
                                placeholder="Search for a location..."
                                style={{ textAlign: "left" }}
                            />

                            {/* Divider */}
                            <hr className="create-event-divider" />

                            {/* What's The Vibe / Activity? */}
                            <h3 className="create-event-section-title"><u>What's The Vibe / Activity?</u></h3>
                            <p style={{ textAlign: "center", color: "#999", fontSize: "10px", marginBottom: "10px" }}>Choose up to 3.</p>
                            <div className="create-event-chips-container">
                                <button
                                    type="button"
                                    className={`create-event-chip ${this.state.vibes.length === 0 ? 'active' : ''}`}
                                    onClick={() => this.setState({ vibes: [] })}
                                >Unsure yet</button>
                                {vibeOptions.map(v => (
                                    <button
                                        type="button"
                                        key={v.label}
                                        className={`create-event-chip ${this.state.vibes.includes(v.label) ? 'active' : ''}`}
                                        onClick={() => this.toggleSelection('vibes', v.label, 3)}
                                    >{v.label}</button>
                                ))}
                            </div>

                            {/* Divider */}
                            <hr className="create-event-divider" />

                            {/* Max Allowed */}
                            <h3 className="create-event-section-title"><u>Max Allowed For This Event?</u></h3>
                            <select
                                className="form-input"
                                style={{ width: "200px" }}
                                value={this.state.max_allowed}
                                onChange={(e) => this.setState({ max_allowed: e.target.value })}
                            >
                                {maxOptions.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>

                            {/* Divider */}
                            <hr className="create-event-divider" />

                            {/* Age Range */}
                            <h3 className="create-event-section-title"><u>Set An Age Range For This Event?</u></h3>
                            <p style={{ textAlign: "center", color: "#999", fontSize: "10px", marginBottom: "10px" }}>Choose up to 3 ranges.</p>
                            <div className="create-event-chips-container">
                                {ageOptions.map(age => (
                                    <button
                                        type="button"
                                        key={age}
                                        className={`create-event-chip ${this.state.age_ranges.includes(age) ? 'active' : ''}`}
                                        onClick={() => this.toggleSelection('age_ranges', age, 3, 'Any age')}
                                    >{age}</button>
                                ))}
                            </div>

                            {/* Divider */}
                            <hr className="create-event-divider" />

                            {/* Who's Welcome */}
                            <h3 className="create-event-section-title"><u>Who's Welcome To This Event?</u></h3>
                            <p style={{ textAlign: "center", color: "#999", fontSize: "10px", marginBottom: "10px" }}>Choose up to 3.</p>
                            <div className="create-event-chips-container">
                                {welcomeOptions.map(w => (
                                    <button
                                        type="button"
                                        key={w}
                                        className={`create-event-chip ${this.state.whos_welcome.includes(w) ? 'active' : ''}`}
                                        onClick={() => this.toggleSelection('whos_welcome', w, 3, 'Anyone')}
                                    >{w}</button>
                                ))}
                            </div>

                            {/* Divider */}
                            <hr className="create-event-divider" />

                            {/* Account Types */}
                            <h3 className="create-event-section-title"><u>Account Types That Can Join?</u></h3>
                            <p style={{ textAlign: "center", fontSize: "11px", marginBottom: "10px" }}>{accountTypeDescriptions[this.state.account_types]}</p>
                            <div className="create-event-chips-container">
                                {accountTypeOptions.map(at => (
                                    <button
                                        type="button"
                                        key={at}
                                        className={`create-event-chip ${this.state.account_types === at ? 'active' : ''}`}
                                        onClick={() => this.setState({ account_types: at })}
                                    >{at}</button>
                                ))}
                            </div>

                            {/* Divider */}
                            <hr className="create-event-divider" />

                            {/* Submit */}
                            <button
                                className="form-button"
                                disabled={this.state.isSubmitting}
                                style={{ marginBottom: "30px" }}
                            >{this.state.isSubmitting ? 'Loading...' : 'Create Event'}</button>

                        </form>
                    </div>
                    <p className="signup-info-footer">© 2026 AniMates. All rights reserved.</p>
                </div>
            </div>
        );
    }
}

export default CreateEventOpportunity;
